<template lang="pug">
div(:class="[hideAdmin ? 'av-hide-admin' : '', 'layout-container']")
  CampaignBanner(
    v-if="shouldShowCampaignBanner"
    :campaign-copy="activeCampaignCopy")
  TheRebrandHeader(v-if="page && page.id")
  AABanner(v-if="hasCarForm")

  Transition(name="ease-in-out" duration="200")
    section.bg-anyvan-semantic-positive(v-if="discountCode.isSet && discountCode.value")
      .content-narrow
        p.av-paragraph.text-center 🎉 {{$t("banner.discount_value.you_have")}} {{ discountCode.value }} {{$t("banner.discount_value.off booking")}}
    section.bg-anyvan-semantic-positive(v-else-if="discountCode.isSet && !discountCode.value")
      .content-narrow
        p.av-paragraph.text-center 🎉 {{$t("banner.discount_value.default")}}
    section.bg-anyvan-semantic-positive(v-else-if="creditCodeValue > 0")
      .content-narrow
        p.av-paragraph.text-center 🎉 {{$t("banner.default.your")}} £{{creditCodeValue}} {{$t("banner.default.discount_applied")}}
  ActivePrelisting(
    v-if="shouldShowActivePrelisting"
    :listings="activePrelistings"
    :show-active-prelistings="showActivePrelistings"
    @banner-close="handleActivePrelistingClose")
  section(
    v-for="(block, key) in blocks"
    :key="`block${key}`"
    v-bind="getAttributes(block)"
    :id="`section-no-${key}`")
    .content
      Block(
        :block="block"
        :block-id="`block-no-${key}`"
        :lazy="key > 2"
        virgin
        :isRebrand="true"
        :isPPC="page?.seo?.metaRobotsNoindex === 'noindex'")
  LiveChatWidget(
    v-if="shouldDisplayLiveChat"
    :license="`${$config.public.liveChatLicense}`"
    visibility="minimized"
    group="19")
  TheFooterContainer(v-if="page && page.id" :page="page" :isRebrand="true")
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { mapGetters } from "vuex"
import { LiveChatWidget } from "@livechat/widget-vue/v2"
import CampaignBanner from "~/components/banner/CampaignBanner.vue"
import AABanner from "~/components/banner/AABanner.vue"
import { CAMPAIGN_AUGUST_URGENCY, HOME_PAGE_HEADER } from "~/constants/blocks"
import { getAttributes, patternCheck } from "~/utils/block"
import { GET_INVENTORY, SET_INVENTORY, SET_WINDOW_WIDTH } from "~/store/pages"
import { GET_CREDIT_CODE_VALUE } from "~/store/credit-code"
import { campaignIds } from "~/constants/campaigns"
import { URL_PARAM_HIDE_ADMIN } from "~/constants/booking-insights"
import { type AnyVanLocale, LOCALE } from "~/constants/locale"
import {
  getPath,
  isResponseIQExcludePage,
  getv4LocaleFromI18nLocale
} from "~/scripts/useful-functions"

type ActiveCampaign = {
  id: string
  supportedLocales: AnyVanLocale[]
  copy: {
    emoji: string
    mobile: {
      text: string
      cta?: string
    }
    desktop: {
      text: string
      cta?: string
    }
  }
}

interface ComponentData {
  blocks: Block[]
  hideAdmin: boolean
  isKeyboardVisible: boolean
  isAndroid: boolean
  discountCode: {
    isSet: boolean
    value: string | undefined
  }
  activeCampaigns: ActiveCampaign[]
  activePrelistings: Record<string, any>[]
  showActivePrelistings: boolean
  activePrelistingLimit: number
  activePrelistingCategory: number
}

type ValidationResult = {
  data: string | undefined
  error: string | undefined
}

export default defineComponent({
  name: "PageLayoutRebrand",

  components: {
    CampaignBanner,
    LiveChatWidget,
    AABanner
  },

  props: {
    page: {
      type: Object as () => Page,
      required: true
    }
  },

  data(): ComponentData {
    return {
      blocks: [],
      hideAdmin: false,
      isKeyboardVisible: false,
      isAndroid: false,
      discountCode: {
        isSet: false,
        value: undefined
      },
      activeCampaigns: [
        {
          id: campaignIds["august-urgency-messaging"],
          supportedLocales: [LOCALE.UK],
          copy: {
            emoji: "🚚",
            mobile: {
              text: "Moving in August? We still have availability!"
            },
            desktop: {
              text: "Moving in August? Great news, we still have availability - it’s going quick!"
            }
          }
        }
      ],
      activePrelistings: [],
      showActivePrelistings: true,
      activePrelistingLimit: 2,
      activePrelistingCategory: 0
    }
  },

  computed: {
    ...mapGetters("pages", {
      getInventory: GET_INVENTORY
    }),
    ...mapGetters("credit-code", {
      creditCodeValue: GET_CREDIT_CODE_VALUE
    }),

    shouldDisplayLiveChat(): boolean {
      return Boolean(
        this.$config.public.liveChatLicense &&
          this.getInventory === false &&
          this.$i18n.locale === LOCALE.UK
      )
    },

    shouldShowActivePrelisting(): boolean {
      return this.activePrelistings.length > 0 && this.showActivePrelistings
    },

    getActiveCampaignForLocale(): ActiveCampaign | undefined {
      if (!this.activeCampaigns.length) {
        return
      }
      const isAugustUrgencyCampaignActive = patternCheck(
        this.blocks[0]?.attributes ?? [],
        CAMPAIGN_AUGUST_URGENCY
      )

      if (!isAugustUrgencyCampaignActive) {
        return
      }

      const activeCampaignId = campaignIds["august-urgency-messaging"]

      return this.activeCampaigns.find(
        campaign =>
          campaign.supportedLocales.includes(this.$i18n.locale as AnyVanLocale) &&
          campaign.id === activeCampaignId
      )
    },
    activeCampaignCopy(): {
      id: string
      emoji: string
      mobile: { text: string; cta: string }
      desktop: { text: string; cta: string }
    } | null {
      const campaign = this.getActiveCampaignForLocale
      if (!campaign) {
        return null
      }
      return {
        id: campaign.id,
        emoji: campaign.copy.emoji,
        mobile: campaign.copy.mobile,
        desktop: campaign.copy.desktop
      }
    },

    shouldShowCampaignBanner(): boolean {
      const isHomepage = patternCheck(this.blocks[0]?.attributes ?? [], HOME_PAGE_HEADER)

      return this.getActiveCampaignForLocale !== undefined && isHomepage
    },

    hasCarForm(): boolean {
      return patternCheck(this.blocks[0]?.attributes ?? [], "aa-banner")
    }
  },

  watch: {
    getInventory: {
      handler(newVal) {
        if (newVal === false) {
          if (!isResponseIQExcludePage(this.$i18n.locale, getPath(this.$route))) {
            // @ts-ignore-next-line
            window.dataLayer?.push({ event: "show_responseiq" })
          }
        }
      }
    }
  },

  created() {
    this.blocks = [...this.page?.blocks]
    this.hideAdmin = this.$route.query[URL_PARAM_HIDE_ADMIN] === "true"

    this.discountCode = {
      isSet: Object.hasOwn(this.$route.query, "discount-value"),
      value: this.validatePercentage(this.$route.query?.["discount-value"]).data
    }
  },

  beforeMount() {
    this.$store.dispatch(`pages/${SET_WINDOW_WIDTH}`, window.innerWidth)
  },

  async mounted() {
    if (this.getInventory === undefined) {
      this.$store.dispatch(`pages/${SET_INVENTORY}`, false)
    }

    // Fetch active prelistings
    await this.fetchActivePrelistings()

    const ua = navigator.userAgent.toLowerCase()
    if (ua.includes("android")) {
      this.isAndroid = true
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", this.updateKeyboardVisibility)
      }
    }
  },
  beforeUnmount() {
    // Clean up the event listener when the component is destroyed
    if (this.isAndroid) {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", this.updateKeyboardVisibility)
      }
    }
  },

  methods: {
    getAttributes,

    async fetchActivePrelistings() {
      if (!useCookie("active_prelistings_banner").value) {
        try {
          const url =
            this.$config.public.v4Host +
            "/ng/getactiveprelistings?load_count=" +
            this.activePrelistingLimit
          const result = await this.$axios.get(url, {
            headers: {
              "X-Requested-With": "XMLHttpRequest",
              "AV-locale": getv4LocaleFromI18nLocale(this.$i18n.locale)
            },
            withCredentials: true
          })
          let listings = result.data?.listings ?? []
          if (this.activePrelistingCategory) {
            listings = listings.filter((listing: Record<string, any>) => {
              return listing.category_id == this.activePrelistingCategory
            })
          }
          this.activePrelistings = listings
        } catch (e) {
          console.log("There was an error fetching active prelistings", e)
        }
      }
    },

    handleActivePrelistingClose() {
      this.showActivePrelistings = false
    },

    updateKeyboardVisibility() {
      if (window.visualViewport) {
        // Check if the keyboard is visible
        this.isKeyboardVisible = window.visualViewport.height < screen.height * 0.6

        if (this.isKeyboardVisible && this.isAndroid) {
          document.body.classList.add("hide-RIQ")
        } else {
          document.body.classList.remove("hide-RIQ")
        }
      }
    },
    validatePercentage(input: unknown): ValidationResult {
      if (typeof input !== "string") {
        return {
          data: undefined,
          error: "Input must be a string"
        }
      }

      if (!input.endsWith("%")) {
        return {
          data: undefined,
          error: 'Must end with "%"'
        }
      }

      const withoutPercent = input.slice(0, -1)

      if (withoutPercent.includes(".")) {
        return {
          data: undefined,
          error: "Must be an integer"
        }
      }

      const numericValue = Number(withoutPercent)

      if (isNaN(numericValue)) {
        return {
          data: undefined,
          error: "Must be a valid number"
        }
      }

      if (!Number.isInteger(numericValue)) {
        return {
          data: undefined,
          error: "Must be an integer"
        }
      }

      if (numericValue < 0 || numericValue > 100) {
        return {
          data: undefined,
          error: "Must be between 0 and 100"
        }
      }

      return {
        data: `${numericValue}%`,
        error: undefined
      }
    }
  }
})
</script>

<style>
@import "assets/css/rebrand.css";
@import "assets/css/navigation.css";
</style>
